import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Car, User, Receipt, Loader2, X } from "lucide-react";
import { api } from "../lib/api";

/**
 * GlobalSearch — omnibox that lives in the Back Office header. Debounced
 * fetch to `/admin/search` returning bookings + customers + vehicles.
 * Keyboard: ⌘K (Mac) / Ctrl+K (Win) opens, Esc closes.
 */
export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  // ⌘K / Ctrl+K to open, Esc to close
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/admin/search`, { params: { q: q.trim() } })
        .then((r) => setResults(r.data))
        .catch(() => setResults({ bookings: [], customers: [], vehicles: [] }))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (open && boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const totalCount = useMemo(() => {
    if (!results) return 0;
    return (results.bookings?.length || 0)
      + (results.customers?.length || 0)
      + (results.vehicles?.length || 0);
  }, [results]);

  const jumpToBooking = (b) => {
    setOpen(false);
    // Navigate to Bookings tab and scroll to the row via jump event
    window.location.hash = "bookings";
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("bo:jump-to-booking", { detail: { id: b.id } })
      );
    }, 400);
  };
  const jumpToCustomer = (c) => {
    setOpen(false);
    window.location.hash = "customers";
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("bo:jump-to-customer", { detail: { id: c.id } })
      );
    }, 400);
  };
  const jumpToVehicle = (v) => {
    setOpen(false);
    window.location.hash = "fleet";
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("bo:jump-to-vehicle", { detail: { id: v.id } })
      );
    }, 400);
  };

  return (
    <div ref={boxRef} className="relative" data-testid="bo-global-search">
      <button
        type="button"
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        data-testid="bo-global-search-trigger"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/12 bg-black/40 text-white/60 hover:text-white hover:border-white/25 transition text-xs"
      >
        <Search size={13} />
        <span className="hidden sm:inline">Search everything…</span>
        <kbd className="hidden sm:inline-block ml-2 px-1.5 py-0.5 rounded bg-white/8 text-[9px] label-caps text-white/50">⌘K</kbd>
      </button>

      {open && (
        <div
          data-testid="bo-global-search-panel"
          className="fixed inset-x-0 top-16 z-[60] mx-auto max-w-2xl px-4 sm:px-0"
        >
          <div className="rounded-2xl border border-white/12 bg-ink-900 shadow-[0_30px_120px_rgba(0,0,0,0.55)] overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
              <Search size={16} className="text-cyan" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="bo-global-search-input"
                placeholder="Reservation, customer, phone, plate, VIN…"
                className="flex-1 bg-transparent outline-none text-white text-sm placeholder:text-white/40"
              />
              {loading ? <Loader2 size={14} className="animate-spin text-white/50" /> : null}
              <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white">
                <X size={14} />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {!q.trim() ? (
                <EmptyState hint="Try a name, phone, plate, VIN, or PR-2026-…" />
              ) : results && totalCount === 0 && !loading ? (
                <EmptyState hint={`No matches for "${q}"`} />
              ) : results ? (
                <div className="divide-y divide-white/8">
                  {results.bookings?.length > 0 && (
                    <Section title="Reservations" icon={Receipt} count={results.bookings.length}>
                      {results.bookings.map((b) => (
                        <ResultRow
                          key={b.id}
                          testId={`bo-search-result-booking-${b.id}`}
                          onClick={() => jumpToBooking(b)}
                          title={`${b.reservation_id || b.id} · ${b.vehicle_name || "—"}`}
                          subtitle={`${b.full_name || "—"} · ${b.customer_phone || b.customer_email || ""}`}
                          right={<StatusTag status={b.status} />}
                        />
                      ))}
                    </Section>
                  )}
                  {results.customers?.length > 0 && (
                    <Section title="Customers" icon={User} count={results.customers.length}>
                      {results.customers.map((c) => (
                        <ResultRow
                          key={c.id}
                          testId={`bo-search-result-customer-${c.id}`}
                          onClick={() => jumpToCustomer(c)}
                          title={c.name || c.email || "—"}
                          subtitle={[c.email, c.phone].filter(Boolean).join(" · ")}
                        />
                      ))}
                    </Section>
                  )}
                  {results.vehicles?.length > 0 && (
                    <Section title="Vehicles" icon={Car} count={results.vehicles.length}>
                      {results.vehicles.map((v) => (
                        <ResultRow
                          key={v.id}
                          testId={`bo-search-result-vehicle-${v.id}`}
                          onClick={() => jumpToVehicle(v)}
                          title={v.name}
                          subtitle={[v.category, v.license_plate, v.vin].filter(Boolean).join(" · ")}
                        />
                      ))}
                    </Section>
                  )}
                </div>
              ) : (
                <div className="px-4 py-6 text-white/50 text-sm">Searching…</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ hint }) {
  return (
    <div className="px-4 py-8 text-center text-white/40 text-sm">
      <Search size={22} className="mx-auto mb-2 opacity-40" />
      {hint}
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <div>
      <div className="px-4 py-2 flex items-center gap-2 text-[9px] label-caps text-white/50 bg-white/[0.02]">
        <Icon size={11} className="text-cyan" /> {title}
        <span className="text-white/30">· {count}</span>
      </div>
      {children}
    </div>
  );
}

function ResultRow({ testId, onClick, title, subtitle, right }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="w-full text-left px-4 py-3 hover:bg-white/[0.04] flex items-center gap-3 transition"
    >
      <div className="flex-1 min-w-0">
        <div className="text-white text-sm truncate">{title}</div>
        {subtitle && <div className="text-white/50 text-[11px] truncate">{subtitle}</div>}
      </div>
      {right}
    </button>
  );
}

const STATUS_COLORS = {
  pending_payment: "bg-amber-400/15 text-amber-300 ring-amber-400/40",
  pending_approval: "bg-amber-400/15 text-amber-300 ring-amber-400/40",
  confirmed: "bg-lime-400/15 text-lime-300 ring-lime-400/40",
  in_progress: "bg-cyan/15 text-cyan ring-cyan/40",
  completed: "bg-white/8 text-white/60 ring-white/15",
  cancelled: "bg-red-500/15 text-red-300 ring-red-500/40",
};
function StatusTag({ status }) {
  if (!status) return null;
  const cls = STATUS_COLORS[status] || "bg-white/8 text-white/60 ring-white/15";
  return (
    <span className={`px-2 py-0.5 rounded-full text-[9px] label-caps ring-1 ${cls}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}
